import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack, VcsProvider } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { isTrackedCommitOutdated } from "../utils";
import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

/**
 * Hook that returns a function to check if the user can sync a commit.
 * User needs to have SpaceWrite or StackSyncCommit action in stack's space
 * Stacks created from a template deployment (blueprintDeployment) cannot sync commits.
 *
 * @returns {(stack: Stack) => AccessResult} A function that takes a stack and returns an AccessResult indicating
 * whether the user can sync the commit, with an error message if applicable.
 */
const useCanSyncCommit = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();
  const hasStackWriteAccess = useHasStackWriteAccess();

  return useCallback(
    (stack: Stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const isRawGitProvider = stack.provider === VcsProvider.Git;

      if (!isTrackedCommitOutdated(stack) && !isRawGitProvider) {
        return { result: false };
      }

      if (hasStackWriteAccess(stack).result) {
        return { result: true };
      }

      if (
        hasAtLeastOneRequiredAction(
          [Action.StackSyncCommit],
          viewer,
          stack.spaceDetails?.id || stack.space
        )
      ) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackWriteAccess, viewer, enableSelfServiceV2]
  );
};

export default useCanSyncCommit;
